/* eslint-disable react/jsx-pascal-case */
import React from "react";
import PropTypes from "prop-types";
import { compose, bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  loadWheelSizes,
  loadServiceLinking,
  loadServiceTypes,
  loadOfferFinancialServices,
  computeOfferFinancialServices,
  saveOfferFinancialServices,
  cancelFinancialServicesChanges,
  changeEditableProperty,
  deleteService
} from "../actionCreators";
import { isMandatory, validationEngine, foreach, isGreaterThan, isLessThan } from "utils/simple-validations";
//sub-components
import OfferFinancialServicesCardExtendedFields from "./OfferFinancialServicesCardExtendedFields";
// core components
import * as numericLimits from "constants/numericLimits";
import { getOfferType } from "features/offer/edit/client/form/selectors";
import { when } from "utils/fn-combinators";
import OfferTypes from "constants/offerType";

// import memoize from 'lodash.memoize';

export const getValidationRules = (offerTypeId = null) => {
  const isCession = offerTypeId === OfferTypes.cession.id;

  return {
    header: {
      agreedMileage: [when(isCession, isMandatory())]
    },
    services: foreach(service => ({
      fromInstallment: [
        isMandatory(),
        isGreaterThan(numericLimits.minimumNaturalNumber),
        isLessThan(service.toInstallment)
      ],
      toInstallment: [isMandatory(), isGreaterThan(service.fromInstallment)],
      periodicity: [isMandatory()]
    })),
    categoryServices: foreach({
      services: foreach(service => ({
        fromInstallment: [
          isMandatory(),
          isGreaterThan(numericLimits.minimumNaturalNumber),
          isLessThan(service.toInstallment)
        ],
        toInstallment: [isMandatory(), isGreaterThan(service.fromInstallment)],
        periodicity: [isMandatory()]
      }))
    })
  };
};

const isNotEmpty = value => {
  return value !== undefined && value !== null && value !== "";
};

export const mergeServiceUpdates = (document, serviceUpdates) => {
  let categoryServicesNew = document.categoryServices.map(a => {
    return Object.assign({}, a, {
      services: a.services.map(b => {
        return Object.assign({}, b);
      })
    });
  });

  serviceUpdates.forEach(a => {
    let category = categoryServicesNew.find(b => b.serviceCategoryId === a.serviceCategoryId);

    if (category && Object.keys(category).length !== 0) {
      let serviceIndex = category.services.findIndex(x => x.serviceTypeId === a.serviceTypeId);

      category.services = Object.assign([...category.services], {
        [serviceIndex]: {
          ...category.services[serviceIndex],
          serviceMonthlyAmount: a.serviceMonthlyAmount,
          percent: a.percent,
          value: a.value
        }
      });

      if (category.services && category.services.filter(a => a.selected).map(s => s.serviceMonthlyAmount).length > 0)
        category.serviceMonthlyAmount = category.services
          .filter(a => a.selected)
          .map(s => s.serviceMonthlyAmount)
          .reduce((acc, a) => acc + a, 0);
    }
  });

  return {
    ...document,
    services: document.services.map(service => {
      const update = serviceUpdates.find(
        x => x.serviceTypeId === service.serviceTypeId && x.serviceCategoryId === null
      );
      return update ? { ...service, ...update } : service;
    }),
    categoryServices: categoryServicesNew
  };
};

export const serviceSelectionUpdater = (serviceTypeId, serviceCategoryId, selected, propName, value) => prevState => {
  let validationInfo = validationEngine.validate(prevState.document, getValidationRules());
  if (isNotEmpty(serviceCategoryId)) {
    const categoryIndex = prevState.document.categoryServices.findIndex(x => x.serviceCategoryId === serviceCategoryId);

    let serviceIndex = null;

    if (categoryIndex === -1) {
      return prevState;
    }

    if (isNotEmpty(serviceTypeId)) {
      serviceIndex = prevState.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === serviceTypeId
      );
      if (categoryIndex === -1 || serviceIndex === -1) {
        return prevState;
      }
    }

    let newServices = null;
    if (isNotEmpty(serviceTypeId)) {
      newServices = Object.assign([...prevState.document.categoryServices[categoryIndex].services], {
        [serviceIndex]: {
          ...prevState.document.categoryServices[categoryIndex].services[serviceIndex],
          selected: selected,
          [propName]: value
        }
      });

      let allServicesSelected = newServices.every(a => a.selected);

      let newCategoryServices = Object.assign([...prevState.document.categoryServices], {
        [categoryIndex]: {
          ...prevState.document.categoryServices[categoryIndex],
          services: newServices,
          isSelected: allServicesSelected,
          serviceMonthlyAmount: newServices
            .filter(a => a.selected)
            .map(s => s.serviceMonthlyAmount)
            .reduce((acc, a) => acc + a, 0)
        }
      });

      if (!validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid) {
        let validationInfoServices = {
          ...validationInfo.validationInfo.categoryServices[categoryIndex].map(s => ({
            ...s,
            isValid: true
          }))
        };
        let validationInfoCategory = {
          ...validationInfo.categoryServices.map(a => ({ ...a, services: validationInfoServices }))
        };
        validationInfo = Object.assign(validationInfo, validationInfoCategory);
      }

      let newDocument = {
        ...prevState.document,
        categoryServices: newCategoryServices
      };

      return {
        document: newDocument,
        validationInfo,
        isDirty: true
      };
    } else {
      let selectedServices = null;
      if (selected)
        selectedServices = prevState.document.categoryServices[categoryIndex].services.map(s => ({
          ...s,
          selected: true
        }));
      else
        selectedServices = prevState.document.categoryServices[categoryIndex].services.map(s => ({
          ...s,
          selected: false
        }));

      let newCategoryServices = Object.assign([...prevState.document.categoryServices], {
        [categoryIndex]: {
          ...prevState.document.categoryServices[categoryIndex],
          services: selectedServices,
          isSelected: selected
        }
      });

      let newDocument = {
        ...prevState.document,
        categoryServices: newCategoryServices
      };

      return {
        document: newDocument,
        validationInfo,
        isDirty: true
      };
    }
  } else {
    const serviceIndex = prevState.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);
    if (serviceIndex === -1) {
      return prevState;
    }

    if (!validationInfo.services[serviceIndex].isValid) {
      validationInfo = {
        ...validationInfo,
        services: Object.assign(
          { ...validationInfo.services },
          { [serviceIndex]: { ...validationInfo.services[serviceIndex], isValid: true } }
        )
      };
    }

    return {
      document: {
        ...prevState.document,
        services: Object.assign([...prevState.document.services], {
          [serviceIndex]: { ...prevState.document.services[serviceIndex], selected: selected, [propName]: value }
        })
      },
      validationInfo,
      isDirty: true
    };
  }
};

export const itemUpdater = (serviceTypeId, serviceCategoryId, propName, value) => prevState => {
  if (isNotEmpty(serviceCategoryId)) {
    const categoryIndex = prevState.document.categoryServices.findIndex(x => x.serviceCategoryId === serviceCategoryId);
    const serviceIndex = prevState.document.categoryServices[categoryIndex].services.findIndex(
      x => x.serviceTypeId === serviceTypeId
    );

    if (categoryIndex === -1 || serviceIndex === -1) {
      return prevState;
    }
    let newServices = Object.assign([...prevState.document.categoryServices[categoryIndex].services], {
      [serviceIndex]: {
        ...prevState.document.categoryServices[categoryIndex].services[serviceIndex],
        [propName]: value
      }
    });

    let newCategoryServices = Object.assign([...prevState.document.categoryServices], {
      [categoryIndex]: { ...prevState.document.categoryServices[categoryIndex], services: newServices }
    });

    let newDocument = {
      ...prevState.document,
      categoryServices: newCategoryServices
    };

    const validationInfo = validationEngine.validate(newDocument, getValidationRules());

    return {
      document: newDocument,
      validationInfo,
      isDirty: true
    };
  } else {
    const serviceIndex = prevState.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);
    if (serviceIndex === -1) {
      return prevState;
    }

    const document = {
      ...prevState.document,
      services: Object.assign([...prevState.document.services], {
        [serviceIndex]: {
          ...prevState.document.services[serviceIndex],
          [propName]: value
        }
      })
    };
    const validationInfo = validationEngine.validate(document, getValidationRules());

    return {
      document,
      validationInfo,
      isDirty: true
    };
  }
};

export const headerUpdater = (prop, value, offerTypeId) => prevState => {
  const document = { ...prevState.document, header: { ...prevState.document.header, [prop]: value } };
  const validationInfo = validationEngine.validate(document, getValidationRules(offerTypeId));
  return {
    document,
    validationInfo,
    isDirty: true
  };
};

export const dispatchHeaderPropertyChangeEffects = (props, state) => {
  let servicesToCompute = state.document.services
    .filter((_, index) => state.validationInfo.services[index].isValid)
    .filter(x => x.selected)
    .map(y => ({ serviceTypeId: y.serviceTypeId, serviceCategoryId: y.serviceCategoryId }));

  let servicesCategoriesToCompute = state.document.categoryServices.map((category, categoryIndex) => {
    const a = category.services
      .filter((__, serviceIndex) => state.validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid)
      .filter(a => a.selected)
      .map(a => ({ serviceTypeId: a.serviceTypeId, serviceCategoryId: a.serviceCategoryId }));
    return a;
  });

  if (servicesCategoriesToCompute.length > 0) {
    let servicesFromCategoriesToCompute = servicesCategoriesToCompute.reduce(selectMany(a => a, []));
    let allServicesToCompute = servicesToCompute.concat(servicesFromCategoriesToCompute);
    dispatchCompute(props, state, allServicesToCompute);
  }
};

export function selectMany(f) {
  return function (acc, b) {
    return acc.concat(f(b));
  };
}

export function getServicesFormCategories(document) {
  let servicesFromCategories = document.categoryServices.reduce(
    selectMany(a => a.services),
    []
  );
  let allServices = document.services.concat(servicesFromCategories);
  return allServices;
}

export const dispatchCompute = (props, state, serviceDetails) => {
  props.actions.computeOfferFinancialServices(props.documentId, props.siteId, state.document.header, serviceDetails);
};

export const dispatchSelectionChangeEffects = (service, props, state) => {
  let validationInfo = validationEngine.validate(state.document, getValidationRules());

  if (isNotEmpty(service.serviceCategoryId)) {
    const categoryIndex = state.document.categoryServices.findIndex(
      x => x.serviceCategoryId === service.serviceCategoryId
    );
    if (isNotEmpty(service.serviceTypeId)) {
      const selected = state.document.categoryServices
        .find(x => x.serviceCategoryId === service.serviceCategoryId)
        .services.find(x => x.serviceTypeId === service.serviceTypeId).selected;
      const serviceIndex = state.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === service.serviceTypeId
      );
      if (
        categoryIndex !== -1 &&
        serviceIndex !== -1 &&
        validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid &&
        selected
      ) {
        const serviceDetail = {
          serviceTypeId: service.serviceTypeId,
          serviceCategoryId: state.document.categoryServices[categoryIndex].services.find(
            x => x.serviceTypeId === service.serviceTypeId
          ).serviceCategoryId,
          period: service.period,
          periodicity: service.periodicity,
          fromInstallment: service.fromInstallment,
          toInstallment: service.toInstallment,
          percent: service.percent
        };
        dispatchCompute(props, state, [serviceDetail]);
      }
    } else {
      const selected = state.document.categoryServices.find(
        x => x.serviceCategoryId === service.serviceCategoryId
      ).isSelected;
      if (categoryIndex !== -1 && validationInfo.categoryServices[categoryIndex].isValid && selected) {
        const serviceDetails = state.document.categoryServices[categoryIndex].services.map(a => ({
          serviceTypeId: a.serviceTypeId,
          serviceCategoryId: a.serviceCategoryId,
          period: a.period,
          periodicity: a.periodicity,
          fromInstallment: a.fromInstallment,
          toInstallment: a.toInstallment,
          percent: a.percent
        }));
        dispatchCompute(props, state, serviceDetails);
      }
    }
  } else {
    const selected = state.document.services.find(x => x.serviceTypeId === service.serviceTypeId).selected;
    const serviceIndex = state.document.services.findIndex(x => x.serviceTypeId === service.serviceTypeId);

    if (serviceIndex !== -1 && validationInfo.services[serviceIndex].isValid && selected) {
      const serviceDetail = {
        serviceTypeId: service.serviceTypeId,
        serviceCategoryId: state.document.services.find(x => x.serviceTypeId === service.serviceTypeId)
          .serviceCategoryId,
        period: service.period,
        periodicity: service.periodicity,
        fromInstallment: service.fromInstallment,
        toInstallment: service.toInstallment,
        percent: service.percent
      };
      dispatchCompute(props, state, [serviceDetail]);
    }
  }
};

const initialValidationInfo = {
  isValid: true,
  services: {},
  categoryServices: {
    services: {}
  }
};

export class OfferFinancialServicesContainerExtendedFields extends React.PureComponent {
  state = {
    document: null,
    validationInfo: initialValidationInfo,
    isDirty: false,
    showModal: false,
    prevProps: {
      document: null,
      serviceUpdates: []
    }
  };
  passedServiceTypeList = [];
  selectionConfirmed = false;
  cancelSelection = false;

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;
    //react to document prop change
    if (props.document !== prevProps.document) {
      return {
        prevProps: { document: props.document, serviceUpdates: props.serviceUpdates },
        document: props.document,
        isValid: true,
        validationInfo: initialValidationInfo,
        isDirty: false,
        editable: false
      };
    }
    //react to serviceUpdates prop change
    if (props.serviceUpdates !== prevProps.serviceUpdates) {
      return {
        prevProps: { ...prevProps, serviceUpdates: props.serviceUpdates },
        document: mergeServiceUpdates(state.document, props.serviceUpdates),
        isDirty: true
      };
    }

    return null;
  }

  componentDidMount() {
    const { actions, documentId, siteId } = this.props;
    actions.loadOfferFinancialServices(documentId, siteId);
    actions.loadWheelSizes(documentId, siteId);
    actions.loadServiceLinking(documentId, siteId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.documentLoaded !== this.props.documentLoaded && !this.props.documentLoaded) {
      this.props.actions.loadOfferFinancialServices(this.props.documentId, this.props.siteId);
      this.props.actions.loadWheelSizes(this.props.documentId, this.props.siteId);
      this.props.actions.loadServiceLinking(this.props.documentId, this.props.siteId);
    }
  }

  componentWillUnmount() {
    this.props.actions.cancelFinancialServicesChanges(this.props.documentId, this.props.siteId);
  }

  deleteService = deletedServiceId => {
    this.props.actions.deleteService(this.props.documentId, this.props.siteId, deletedServiceId);
  };

  onCloseAvailableServicesModal = () => {
    this.setState({
      showModal: false
    });
  };

  onAddServices = () => {
    this.setState({
      showModal: true
    });
  };

  handleServiceSelectionChange = (serviceTypeId, serviceCategoryId, selected) => {
    this.passedServiceTypeList = [];
    this.selectionConfirmed = false;
    this.cancelSelection = false;
    const categoryIndex = this.props.document.categoryServices.findIndex(
      x => x.serviceCategoryId === serviceCategoryId
    );

    let serviceIndex = null;

    if (isNotEmpty(serviceTypeId) && categoryIndex > -1) {
      serviceIndex = this.props.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === serviceTypeId
      );
    }
    if (serviceIndex !== null) {
      const serviceTypeId = this.props.document.categoryServices[categoryIndex].services[serviceIndex].serviceTypeId;
      this.processLinkedServices(serviceTypeId, selected);
    }
    if (categoryIndex > -1 && serviceIndex === null) {
      this.props.document.categoryServices[categoryIndex].services.forEach(service => {
        this.processLinkedServices(service.serviceTypeId, selected);
      });
    }
    if (isNotEmpty(serviceTypeId) && categoryIndex === -1) {
      serviceIndex = this.props.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);
      this.processLinkedServices(this.props.document.services[serviceIndex].serviceTypeId, selected);
    }
  };

  processLinkedServices = (serviceTypeId, selected) => {
    const linkedServices = this.props.serviceLinkingList;
    const service = [];
    this.props.document.categoryServices.forEach(categService => {
      const sourceCategService = categService.services.filter(service => service.serviceTypeId === serviceTypeId);
      if (sourceCategService.length > 0) service.push(...sourceCategService);
    });
    const sourceService = this.props.document.services.filter(service => service.serviceTypeId === serviceTypeId);
    if (sourceService.length > 0) service.push(...sourceService);

    this.passedServiceTypeList.push(service[0].serviceTypeId);
    const listServiceLinked = linkedServices.filter(
      x => x.destServiceTypeId === serviceTypeId || x.sourceServiceTypeId === serviceTypeId
    );
    if (listServiceLinked.length === 0) {
      this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
        dispatchSelectionChangeEffects(service[0], this.props, this.state)
      );
      return;
    }

    this.confirmSelection(listServiceLinked, service, selected);
    if (!this.cancelSelection)
      this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
        dispatchSelectionChangeEffects(service[0], this.props, this.state)
      );

    listServiceLinked.forEach(x => {
      let servicesToLink = [];
      this.props.document.categoryServices.forEach(categService => {
        const sourceCategService = categService.services.filter(
          service => service.serviceTypeId === x.sourceServiceTypeId || service.serviceTypeId === x.destServiceTypeId
        );
        if (sourceCategService.length > 0) {
          servicesToLink.push(...sourceCategService);
        }
      });

      servicesToLink.forEach(serviceToLink => {
        if (this.passedServiceTypeList.filter(x => x === serviceToLink.serviceTypeId).length === 0)
          if (x.linkingTypeCode === "EXCLUDE") {
            if (selected === true)
              this.processLinkedServices(serviceToLink.serviceTypeId, serviceToLink.serviceCategoryId, !selected);
          } else this.processLinkedServices(serviceToLink.serviceTypeId, serviceToLink.serviceCategoryId, selected);
      });
    });
  };

  confirmSelection = (listServiceLinked, service, selected) => {
    if (!this.selectionConfirmed) {
      let message = "";
      listServiceLinked.forEach(serv => {
        if (serv.linkingTypeCode === "LINK") {
          message =
            message +
            this.props.t("OfferEdit.FinancialServices.LinkService", {
              serv: {
                sourceServiceName: serv.sourceServiceName || "",
                destServiceName: serv.destServiceName || ""
              }
            }) +
            "\n";
        } else
          message =
            message +
            this.props.t("OfferEdit.FinancialServices.ExcludeService", {
              serv: {
                sourceServiceName: serv.sourceServiceName || "",
                destServiceName: serv.destServiceName || ""
              }
            }) +
            "\n";
      });

      if (listServiceLinked.length > 0) {
        this.selectionConfirmed = true;
        if (window.confirm(message + this.props.t("OfferEdit.FinancialServices.ConfirmationMessage"))) {
          this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
            dispatchSelectionChangeEffects(service[0], this.props, this.state)
          );
        } else {
          this.cancelSelection = true;
          return;
        }
      } else
        this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
          dispatchSelectionChangeEffects(service[0], this.props, this.state)
        );
    }
  };

  handleServiceItemPropertyChange = (serviceTypeId, serviceCategoryId, propName, value) => {
    if (propName !== undefined) {
      this.setState(itemUpdater(serviceTypeId, serviceCategoryId, propName, value));
    }
  };

  handleHeaderPropertyChange = (prop, offerTypeId) => value => {
    this.setState(headerUpdater(prop, value, offerTypeId), () =>
      dispatchHeaderPropertyChangeEffects(this.props, this.state)
    );
  };

  handleSave = offerTypeId => {
    const validationInfo = validationEngine.validate(this.state.document, getValidationRules(offerTypeId));
    if (!validationInfo.isValid) {
      this.setState({
        validationInfo
      });
      return;
    }

    this.props.actions.saveOfferFinancialServices(
      this.props.documentId,
      this.props.siteId,
      this.state.document,
      getServicesFormCategories(this.state.document)
    );
  };

  handleServiceItemComputeValue = service => {
    dispatchSelectionChangeEffects(service, this.props, this.state);
  };

  handleModifyData = () => {
    this.props.actions.changeEditableProperty(true);
  };

  handleCancelModifyData = () => {
    const { actions, documentId, siteId } = this.props;
    const { prevProps } = this.state;

    actions.cancelFinancialServicesChanges(documentId, siteId);
    actions.loadOfferFinancialServices(documentId, siteId);
    this.setState(prevProps.document);
  };

  render() {
    const {
      wheelSizes,
      saving,
      servicesLoading,
      editable,
      documentId,
      siteId,
      offerTypeId,
      documentLoaded,
      allowedContractTypesForAddingServices
    } = this.props;
    const { document, isDirty, validationInfo } = this.state;
    return (
      <OfferFinancialServicesCardExtendedFields
        document={document}
        documentId={documentId}
        siteId={siteId}
        servicesLoading={servicesLoading}
        documentLoaded={documentLoaded}
        wheelSizes={wheelSizes}
        validationInfo={validationInfo}
        showModal={this.state.showModal}
        onServiceSelectionChange={this.handleServiceSelectionChange}
        onHeaderPropertyChange={this.handleHeaderPropertyChange}
        onSave={this.handleSave}
        isDirty={isDirty}
        saving={saving}
        onCloseAvailableServicesModal={this.onCloseAvailableServicesModal}
        onAddServices={this.onAddServices}
        loadServiceTypesList={this.loadServiceTypesList}
        deleteService={this.deleteService}
        onServiceItemPropertyChange={this.handleServiceItemPropertyChange}
        onServiceItemComputeValue={this.handleServiceItemComputeValue}
        editable={editable}
        handleModifyData={this.handleModifyData}
        offerTypeId={offerTypeId}
        handleCancelModifyData={this.handleCancelModifyData}
        allowedContractTypesForAddingServices={allowedContractTypesForAddingServices}
      />
    );
  }
}

OfferFinancialServicesContainerExtendedFields.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  document: PropTypes.object.isRequired,
  serviceUpdates: PropTypes.array.isRequired,
  wheelSizes: PropTypes.array.isRequired,
  serviceLinkingList: PropTypes.array.isRequired,
  serviceTypesList: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  servicesLoading: PropTypes.array.isRequired,
  documentLoaded: PropTypes.bool.isRequired,
  editable: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  offerTypeId: PropTypes.number.isRequired,
  allowedContractTypesForAddingServices: PropTypes.string.isRequired
};

export function mapStateToProps(state) {
  const {
    document,
    serviceUpdates,
    wheelSizes,
    serviceLinkingList,
    serviceTypesList,
    saving,
    servicesLoading,
    documentLoaded,
    editable
  } = state.currentOffer.financialServicesData;
  const offerTypeId = getOfferType(state);
  const { allowedContractTypesForAddingServices } = state.params.items;
  return {
    document,
    serviceUpdates,
    wheelSizes,
    serviceLinkingList,
    serviceTypesList,
    saving,
    servicesLoading,
    documentLoaded,
    editable,
    offerTypeId,
    allowedContractTypesForAddingServices
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadOfferFinancialServices,
        computeOfferFinancialServices,
        saveOfferFinancialServices,
        cancelFinancialServicesChanges,
        loadWheelSizes,
        loadServiceLinking,
        loadServiceTypes,
        changeEditableProperty,
        deleteService
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(OfferFinancialServicesContainerExtendedFields);
